import React, { useState } from 'react';
import { GalleryItem } from '@/types';
import './Modals.css';

interface NewGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (gallery: Omit<GalleryItem, 'id' | 'createdAt' | 'updatedAt'>) => void;
  isSubmitting?: boolean;
}

const NewGalleryModal: React.FC<NewGalleryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false
}) => {
  const [title, setTitle] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>(['']);
  const [newImageUrl, setNewImageUrl] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validImageUrls = imageUrls.filter(url => url.trim() !== '');
    if (validImageUrls.length === 0) return;
    
    onSubmit({
      title: title.trim(),
      images: validImageUrls,
      date: new Date().toISOString().split('T')[0]
    });
  };

  const addImageUrl = () => {
    if (newImageUrl.trim() !== '') {
      setImageUrls([...imageUrls, newImageUrl.trim()]);
      setNewImageUrl('');
    }
  };

  const removeImageUrl = (index: number) => {
    const newImageUrls = [...imageUrls];
    newImageUrls.splice(index, 1);
    setImageUrls(newImageUrls);
  };

  const updateImageUrl = (index: number, value: string) => {
    const newImageUrls = [...imageUrls];
    newImageUrls[index] = value;
    setImageUrls(newImageUrls);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create New Gallery</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="gallery-form">
          <div className="form-group">
            <label htmlFor="title">Gallery Title</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter gallery title"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Image URLs</label>
            {imageUrls.map((url, index) => (
              <div key={index} className="image-url-input">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => updateImageUrl(index, e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  required={index === 0}
                />
                {index > 0 && (
                  <button 
                    type="button" 
                    className="remove-button"
                    onClick={() => removeImageUrl(index)}
                    disabled={isSubmitting}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            
            <div className="add-image-url">
              <input
                type="url"
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                placeholder="Add another image URL"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addImageUrl())}
              />
              <button 
                type="button" 
                className="add-button"
                onClick={addImageUrl}
                disabled={!newImageUrl.trim() || isSubmitting}
              >
                Add Image
              </button>
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting || !title.trim() || imageUrls.filter(url => url.trim() !== '').length === 0}
            >
              {isSubmitting ? 'Creating...' : 'Create Gallery'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewGalleryModal;
